import React, { useEffect, useState } from "react";
import {
  Create,
  CreateProps,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  useDataProvider,
  useNotify,
} from "react-admin";
import { Helmet } from "react-helmet";
import GoogleMapReact from "google-map-react";
import Marker from "./components/Marker";
import { SchoolIdSelected } from "utils/tools";
import _ from "lodash";
import { Typography, Grid, Chip, Backdrop } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { GridinRow } from "utils/style";
import moment from "moment";
import SaveRoutePlanButton from "./components/SaveRoutePlanButton";
import { useWindowDimensions } from "utils/dimensionHook";
import { useStyles } from "./style";
import { getStudents, getStudentAddressesByStudentIds } from "./tools";
import LoadingComponent from "./components/LoadingComponent";
import LoadingProgressComponent from "./components/LoadingProgressComponent";

const RoutePlanCreate = (props: CreateProps) => {
  const classes = useStyles({ openRoutePanel: false });
  const provider = useDataProvider();
  const notify = useNotify();
  const schoolIdSelected = SchoolIdSelected();
  const { width } = useWindowDimensions();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState<boolean>(false);
  const [progressValue, setProgressValue] = useState<number>(0);

  const [busScheduleId, setBusScheduleId] = useState<number>();
  const [busSchedule, setBusSchedule] = useState<IBusSchedule>();
  const [students, setStudents] = useState<IStudent[]>([]);
  const [studentAddresses, setStudentAddresses] = useState<IStudentAddress[]>(
    []
  );
  const [markers, setMarkers] = useState<any>([]);
  const [schools, setSchools] = useState([]);
  const [selectedMarkers, setSelectedMarkers] = useState<any>([]);

  // set selected BusSchedule
  useEffect(() => {
    if (busScheduleId !== undefined && busScheduleId > 0) {
      provider.getOne("busSchedules", { id: busScheduleId }).then((result) => {
        setBusSchedule(result.data as IBusSchedule);

        provider
          .getOne("schools", { id: result.data.schoolId })
          .then((result) => {
            setSchools([result.data as ISchool]);
          });
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busScheduleId]);

  // set data for rendering markers
  useEffect(() => {
    if (busSchedule) {
      if (busSchedule.levels.length > 0) {
        setIsLoading(true);
        fetchAllLevelStudent();
      } else {
        setIsLoading(false);
        setMarkers([]);
        notify("Class Schedule doesn't have any levels", "warning");
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busSchedule]);

  const fetchAllLevelStudent = async () => {
    let allStudents: IStudent[] = [];
    await Promise.all(
      busSchedule.levels.map(async (level: ILevel, index: number) => {
        const results: any = await getStudents(Number(level.id), provider);

        if (results.length > 0) {
          allStudents = [...allStudents, ...results];
        }
      })
    );
    setStudents(allStudents);
  };

  useEffect(() => {
    if (students.length > 0) {
      setIsLoading(false);
      setIsLoadingProgress(true);
      const studentIds: any = students.map((student) => student.id);
      const _chunk = Math.ceil(studentIds.length / 100);
      chunkFetch(studentIds, _chunk);
    }
  }, [students]);

  // set studentAddresses based of student
  const chunkFetch = async (studentIds: any, chunk: number) => {
    let studentIdsSplice = [...studentIds];
    let _studentAddress = [...studentAddresses];

    for (let i = 0; i < chunk; i++) {
      let spliceStudentIds = studentIdsSplice.splice(0, 100);
      console.log(spliceStudentIds);

      const data: any = await getStudentAddressesByStudentIds(
        spliceStudentIds,
        provider
      );

      _studentAddress = [..._studentAddress, ...data];
      setProgressValue(_studentAddress.length);
      console.log(_studentAddress);
    }
    setIsLoadingProgress(false);
    setIsLoading(true);
    setStudentAddresses(_studentAddress);
  };

  // set markers based of studentAddresses
  // and group by PostalCode for student have a same address
  useEffect(() => {
    let tempMarkers: any = [];

    const groupAddress = _.groupBy(studentAddresses, "postalCode");

    Object.keys(groupAddress).forEach((key: any) => {
      const findAddresses = _.filter(studentAddresses, { postalCode: key });

      const tempAddress: any = {
        students: [],
        ...findAddresses[0],
      };

      delete tempAddress.studentId;

      findAddresses.forEach((findAddress: any) => {
        const findStudent = _.find(students, ["id", findAddress.studentId]);
        tempAddress.students.push(findStudent);
      });

      tempMarkers.push(tempAddress);
    });

    setMarkers(tempMarkers);
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentAddresses]);

  const resetWaveSelected = () => {
    setSelectedMarkers([]);
    setStudentAddresses([]);
    setProgressValue(0);
  };

  return (
    <>
      <Helmet>
        <title>Route Plan</title>
      </Helmet>
      <Create {...props}>
        <Grid container>
          <Grid item xs={width <= 1440 ? 5 : 4} className={classes.panel}>
            <SimpleForm toolbar={null}>
              <SanitizedGrid container spacing={2}>
                <Grid item xs={8} style={GridinRow}>
                  <Typography variant="h6" gutterBottom>
                    Route Plan
                  </Typography>
                  <Chip
                    label="Draft"
                    color="primary"
                    className={classes.statusPlanChip}
                  />
                </Grid>

                <Grid item xs={4}>
                  <SaveRoutePlanButton
                    isSaveLoading={(value: boolean) => setIsLoading(value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">Wave</Typography>
                </Grid>
                <Grid item xs={12}>
                  <ReferenceInput
                    source="busScheduleId"
                    reference="busSchedules"
                    label="Select Wave *"
                    filter={{
                      SchoolId: Number(schoolIdSelected),
                      HasRoutePlan: false,
                    }}
                    onChange={({ target }) => {
                      setBusScheduleId(target.value);
                      resetWaveSelected();
                    }}
                  >
                    <SelectInput
                      optionText={(record) => `${record.name} | ${record.type}`}
                    />
                  </ReferenceInput>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Arrive by :{" "}
                    {busSchedule
                      ? moment(busSchedule.arrivalTime, "HH:mm").format(
                          "hh:mm A"
                        )
                      : ""}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Days of the Weeks :{" "}
                    {busSchedule
                      ? `${(() => {
                          let days: string = "";

                          if (busSchedule.isMonday) {
                            days = days + " M,";
                          }
                          if (busSchedule.isTuesday) {
                            days = days + " T,";
                          }
                          if (busSchedule.isWednesday) {
                            days = days + " W,";
                          }
                          if (busSchedule.isThursday) {
                            days = days + " TH,";
                          }
                          if (busSchedule.isFriday) {
                            days = days + " F,";
                          }

                          return days.replace(/,(?=[^,]*$)/, "");
                        })()}`
                      : ""}
                  </Typography>
                </Grid>
              </SanitizedGrid>
            </SimpleForm>
          </Grid>

          <Grid item xs={width <= 1440 ? 7 : 8}>
            <div className={classes.mapWrapper}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: "AIzaSyDYGvy_HvsUfvJ_7_d4PmOH201jIFNn-iM",
                }}
                defaultCenter={{
                  lat: 1.35896,
                  lng: 103.852309,
                }}
                defaultZoom={12}
                yesIWantToUseGoogleMapApiInternals
              >
                {markers.map((marker: any, key: number) => {
                  if (marker.students.length === 0) {
                    return <></>;
                  }

                  const findSelectedMarker = _.find(selectedMarkers, [
                    "id",
                    marker.id,
                  ]);

                  if (!_.isEmpty(findSelectedMarker)) {
                    const findMarker = _.find(markers, [
                      "postalCode",
                      marker.postalCode,
                    ]);
                    findMarker.isSelected = true;
                  } else {
                    const findMarker = _.find(markers, [
                      "postalCode",
                      marker.postalCode,
                    ]);
                    findMarker.isSelected = false;
                  }

                  marker.isSidePanelOpen = false;

                  return (
                    <Marker
                      lat={marker.lat}
                      lng={marker.lng}
                      key={`marker-${key}`}
                      isSelected={marker.isSelected ? true : false}
                      isSidePanelOpen={marker.isSidePanelOpen ? true : false}
                      resource={marker}
                    />
                  );
                })}

                {schools.map((school: any) => (
                  <Marker
                    lat={school.lat}
                    lng={school.lng}
                    key={`school-marker-${school.postalCode}`}
                    resource={school}
                    isSchool
                  />
                ))}
              </GoogleMapReact>
            </div>
          </Grid>
        </Grid>
      </Create>

      <Backdrop
        className={classes.backdropLoading}
        open={isLoading || isLoadingProgress}
      >
        {isLoading && <LoadingComponent />}
        {isLoadingProgress && (
          <LoadingProgressComponent
            progValue={progressValue}
            value={students.length}
          />
        )}
      </Backdrop>
    </>
  );
};

export default RoutePlanCreate;
